import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Gift, Calendar } from "lucide-react";
import PrivacyNotice from "@/components/PrivacyNotice";


function isoLocalTomorrow9am() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  // Format for datetime-local: YYYY-MM-DDTHH:mm
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}


export default function GiftDialog({ open, onOpenChange, bookId, bookTitle, bookAuthor, price }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [senderName, setSenderName] = useState(user?.name || "");
  const [message, setMessage] = useState("");
  const [schedule, setSchedule] = useState(false);
  const [deliverAt, setDeliverAt] = useState(isoLocalTomorrow9am());
  const [loading, setLoading] = useState(false);

  const startCheckout = async () => {
    if (!user) {
      navigate("/login", { state: { from: `/book/${bookId}` } });
      return;
    }
    if (!recipientEmail || !recipientName || !senderName) {
      toast.error("Please fill in the recipient and sender names + email.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        book_id: bookId,
        origin_url: window.location.origin,
        recipient_email: recipientEmail,
        recipient_name: recipientName,
        sender_name: senderName,
        message,
      };
      if (schedule && deliverAt) {
        // datetime-local produces a local ISO — convert to UTC ISO
        payload.deliver_at = new Date(deliverAt).toISOString();
      }
      const { data } = await api.post("/gifts/checkout", payload);
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start gift checkout");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="gift-dialog"
        className="max-w-lg bg-paper border border-edge rounded-none"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <Gift className="w-4 h-4 text-terracotta" />
            <span className="label-eyebrow text-sage">Gift a book</span>
          </div>
          <DialogTitle className="font-serif text-3xl leading-tight">
            Send <em className="not-italic text-terracotta">{bookTitle}</em>
          </DialogTitle>
          <DialogDescription className="text-muted_ink text-sm">
            by {bookAuthor} · ${Number(price).toFixed(2)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow block mb-1">From</label>
              <input
                data-testid="gift-sender-name"
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                placeholder="Your name"
                className="w-full border border-edge bg-white px-3 py-2 text-sm focus:outline-none focus:border-ink"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-1">To</label>
              <input
                data-testid="gift-recipient-name"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="Friend's name"
                className="w-full border border-edge bg-white px-3 py-2 text-sm focus:outline-none focus:border-ink"
              />
            </div>
          </div>
          <div>
            <label className="label-eyebrow block mb-1">Friend's email</label>
            <input
              data-testid="gift-recipient-email"
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="them@example.com"
              className="w-full border border-edge bg-white px-3 py-2 text-sm focus:outline-none focus:border-ink"
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-1">A note (optional)</label>
            <textarea
              data-testid="gift-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Thought of you when I read this…"
              className="w-full border border-edge bg-white px-3 py-2 text-sm focus:outline-none focus:border-ink resize-none"
            />
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              data-testid="gift-schedule-toggle"
              checked={schedule}
              onChange={(e) => setSchedule(e.target.checked)}
              className="accent-terracotta"
            />
            <Calendar className="w-3.5 h-3.5 text-muted_ink" />
            <span>Schedule delivery for later (birthday, holiday…)</span>
          </label>

          {schedule && (
            <input
              data-testid="gift-deliver-at"
              type="datetime-local"
              value={deliverAt}
              onChange={(e) => setDeliverAt(e.target.value)}
              className="w-full border border-edge bg-white px-3 py-2 text-sm focus:outline-none focus:border-ink"
            />
          )}

          <div className="border-t border-edge pt-4 flex items-center justify-between">
            <div>
              <div className="label-eyebrow">You pay</div>
              <div className="font-serif text-3xl">${Number(price).toFixed(2)}</div>
            </div>
            <button
              data-testid="gift-pay-btn"
              onClick={startCheckout}
              disabled={loading}
              className="btn-primary disabled:opacity-60"
            >
              {loading ? "Starting…" : "Pay & send"}
            </button>
          </div>
          <PrivacyNotice
            testid="gift-privacy"
            purpose="We use the recipient's email only to deliver this gift."
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
